import axios from "axios";

/**
 * Resolve the API base URL at runtime — NOT at build time.
 *
 * The frontend is served by Emergent's ingress, which routes `/api/*` on the
 * same host to the backend. That means on ANY deployed environment (preview
 * or production), same-origin relative calls always reach the right backend
 * regardless of what `REACT_APP_BACKEND_URL` was baked into the bundle.
 *
 * We only honour the env var for local dev, where the React dev server runs
 * on :3000 and FastAPI runs on :8001 — different origins by necessity.
 *
 * This immunises production against a mis-baked build-time env var.
 */
function resolveBackendOrigin() {
  if (typeof window !== "undefined" && window.location) {
    const { protocol, hostname, origin } = window.location;
    if (
      protocol === "http:" &&
      (hostname === "localhost" || hostname === "127.0.0.1")
    ) {
      return process.env.REACT_APP_BACKEND_URL || origin;
    }
    return origin;
  }
  return process.env.REACT_APP_BACKEND_URL || "";
}

export const API_BASE_URL = resolveBackendOrigin();

const api = axios.create({
  baseURL: `${API_BASE_URL}/api`,
  withCredentials: true,
  headers: { "Content-Type": "application/json" },
});

// Impersonation: if sessionStorage has an impersonation token, use it as Bearer
api.interceptors.request.use((config) => {
  const impToken = sessionStorage.getItem("impersonate_token");
  if (impToken) {
    config.headers.Authorization = `Bearer ${impToken}`;
  }
  return config;
});

// Silent token refresh on 401
let isRefreshing = false;
let refreshQueue = [];

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config;

    // Skip refresh for impersonation, login, register, refresh endpoints
    if (
      sessionStorage.getItem("impersonate_token") ||
      original.url?.includes("/auth/refresh") ||
      original.url?.includes("/auth/login") ||
      original.url?.includes("/auth/register") ||
      original._retried
    ) {
      return Promise.reject(error);
    }

    if (error.response?.status === 401) {
      if (isRefreshing) {
        // Queue subsequent requests while refresh is in progress
        return new Promise((resolve, reject) => {
          refreshQueue.push({ resolve, reject });
        }).then(() => api(original));
      }

      original._retried = true;
      isRefreshing = true;

      try {
        await api.post("/auth/refresh");
        // Refresh succeeded — retry all queued requests
        refreshQueue.forEach((p) => p.resolve());
        refreshQueue = [];
        return api(original);
      } catch {
        // Refresh failed — reject all queued and redirect to login
        refreshQueue.forEach((p) => p.reject(error));
        refreshQueue = [];
        return Promise.reject(error);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export function formatApiError(error) {
  const detail = error?.response?.data?.detail;
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default api;
